import { useRouter } from 'expo-router';
import {
  ActionWrapper,
  CardContainer,
  Details,
  ItemName,
} from 'features/menu/components/menu-reorder-widget/menu-reorder-card/styled';
import { useAddMappedEntryToCart } from 'features/menu/components/product-wizard/use-add-to-cart';
import { logAddRecentItemToCartEvent } from 'features/order/utils/analytics';
import { useStoreContext } from 'features/store';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { ItemThumbnailWithBadge } from 'components/item-thumbnail-with-badge/item-thumbnail-with-badge';
import { toaster } from 'components/toaster/toaster';
import { Typography } from 'components/ui';
import { ItemAvailabilityStatus } from 'enums/menu';
import useEffectOnce from 'hooks/use-effect-once';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { HapticsImpactStyle } from 'utils/haptic';
import { hapticImpact } from 'utils/haptic/haptic';
import { routes } from 'utils/routing';

import { MinimalMappedOrder } from './minimal-menu-reorder-widget-types';

export const MinimalMenuReorderCard = ({
  order,
  toggleDetailsDrawer,
  drawerIsOpen,
}: {
  order: MinimalMappedOrder;
  toggleDetailsDrawer: () => void;
  drawerIsOpen: boolean;
}) => {
  const { replace } = useRouter();
  const { store } = useStoreContext();
  const addMappedEntryToCart = useAddMappedEntryToCart();
  const isMobile = useIsMobileBreakpoint();

  const linkTextKey = drawerIsOpen ? 'hideDetails' : 'viewDetails';
  const linkText = t(linkTextKey);

  const logViewEvent = () => {
    logEvent(CustomEventNames.LAST_ORDER_HOME_VIEW, EventTypes.Other);
  };
  const logReorderEvent = () => logEvent(CustomEventNames.LAST_ORDER_REORDER, EventTypes.Other);
  const logOpenDetailEvent = () => logEvent(CustomEventNames.LAST_ORDER_DETAILS, EventTypes.Other);

  const totalItems = order.orderEntries.reduce((total, entry) => total + (entry.quantity ?? 1), 0);
  const itemNames = order.orderEntries.map(item => item.name).join(' - ');
  const itemHighlighted = order.orderEntries[0];
  const availableEntries = order.orderEntries.filter(
    entry => entry.status === ItemAvailabilityStatus.AVAILABLE
  );

  useEffectOnce(() => {
    logViewEvent();
  });

  const handleReorder = () => {
    if (drawerIsOpen) {
      toggleDetailsDrawer();
    }

    const addedEntries = availableEntries.filter(entry => {
      const added = addMappedEntryToCart(entry);
      if (added) {
        logAddRecentItemToCartEvent(entry?.name ?? '', store);
      }
      return added;
    });

    const [firstEntry] = addedEntries;
    if (firstEntry) {
      const message =
        addedEntries.length === 1
          ? firstEntry.quantity > 1
            ? t('addToCartMultipleSuccess', {
                itemName: firstEntry.name,
                quantity: firstEntry.quantity - 1,
              })
            : t('addToCartSuccess', { itemName: firstEntry.name })
          : t('cartUpdatedRecentReorder', 'Your cart was updated');

      toaster.addToCartToast(message, firstEntry.image);
    }

    if (addedEntries.length) {
      replace(routes.cart);
      hapticImpact({ style: HapticsImpactStyle.Light });
      logReorderEvent();
    }
  };

  const isReorderingDisabled = !availableEntries.length;

  if (isReorderingDisabled) {
    return null;
  }

  return (
    <CardContainer>
      {itemHighlighted && (
        <ItemThumbnailWithBadge
          item={{
            name: itemHighlighted.name,
            image: itemHighlighted.image as never,
          }}
          totalItems={totalItems}
        />
      )}
      <Details>
        <ItemName numberOfLines={isMobile ? 2 : 1} maxFontSizeMultiplier={1.2}>
          {itemNames}
        </ItemName>
        <Typography
          variant="link"
          maxFontSizeMultiplier={1.2}
          onPress={() => {
            toggleDetailsDrawer();
            logOpenDetailEvent();
          }}
        >
          {linkText}
        </Typography>
      </Details>
      <ActionWrapper>
        <ActionButton
          testID="reorder-button"
          onPress={handleReorder}
          disabled={isReorderingDisabled}
          size={ActionButtonSizes.SMALL}
          accessibilityLabel={
            order.orderNumberFormatted
              ? t('reorderWithOrderNumber', { ticketNumber: order.orderNumberFormatted })
              : t('reorder')
          }
        >
          {t('reorder')}
        </ActionButton>
      </ActionWrapper>
    </CardContainer>
  );
};
